import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Label, Input, Textarea, Select, PrimaryBtn, GhostBtn, Modal } from "@/components/admin/ui";
import ResultsEditor from "@/components/admin/ResultsEditor";
import ImageUploader from "@/components/admin/ImageUploader";
import { Plus, Pencil, Trash2, Settings, ChevronLeft, Flag, Clock, CheckCircle2, XCircle } from "lucide-react";

const DEFAULT_POINTS = { 1: 25, 2: 18, 3: 15, 4: 12, 5: 10, 6: 8, 7: 6, 8: 4, 9: 2, 10: 1 };
const SESSION_OPTS = [
  { v: "gara", l: "Gara" },
  { v: "prove_libere", l: "Prove Libere" },
  { v: "qualifica", l: "Qualifica" },
];
const emptyChamp = { name: "", category: "rc", platform: "", description: "", image_url: "", regulation: "", points: DEFAULT_POINTS, quali_points: null, access: "open" };

function StatusBadge({ status }) {
  if (status === "approved")
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-green-400"><CheckCircle2 className="h-3 w-3" /> Pubblico</span>;
  if (status === "rejected")
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-red-400"><XCircle className="h-3 w-3" /> Rifiutato</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-yellow-400"><Clock className="h-3 w-3" /> In attesa</span>;
}

export default function ChampionshipsPanel({ endpoint = "/championships", showStatus = false, createKind = "community" }) {
  const [champs, setChamps] = useState([]);
  const [editing, setEditing] = useState(null); // champ obj or emptyChamp
  const [selected, setSelected] = useState(null); // managing events for this champ

  const load = () => api.get(endpoint).then((r) => setChamps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Eliminare il campionato e tutti i suoi Gran Premi/risultati?")) return;
    await api.delete(`/championships/${id}`);
    toast.success("Campionato eliminato");
    load();
    if (selected?.id === id) setSelected(null);
  };

  if (selected) {
    return <EventsManager champ={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-bold uppercase text-zinc-100">Campionati</h2>
        <PrimaryBtn onClick={() => setEditing({ ...emptyChamp })} data-testid="new-championship-btn">
          <Plus className="h-4 w-4" /> Nuovo
        </PrimaryBtn>
      </div>

      {champs.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Nessun campionato. Creane uno.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {champs.map((c) => (
            <div key={c.id} data-testid={`admin-champ-${c.id}`} className="border border-zinc-800 bg-zinc-900 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.15em] uppercase text-cyan-400">
                  <Flag className="h-3 w-3" /> {c.category === "rc" ? "RC" : `SIM · ${c.platform || "—"}`}
                </div>
                {showStatus && <StatusBadge status={c.status} />}
              </div>
              <h3 className="font-heading text-lg font-bold text-zinc-50">{c.name}</h3>
              <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{c.description || "—"}</p>
              {showStatus && c.status === "rejected" && c.review_note && (
                <p className="mt-2 text-xs text-red-400">Nota: {c.review_note}</p>
              )}
              <div className="mt-4 flex gap-2">
                <GhostBtn onClick={() => setSelected(c)} data-testid={`manage-champ-${c.id}`} className="flex-1">
                  <Settings className="h-3.5 w-3.5" /> Gestisci
                </GhostBtn>
                <button onClick={() => setEditing(c)} data-testid={`edit-champ-${c.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-yellow-400 hover:border-yellow-400 transition-colors duration-200">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(c.id)} data-testid={`delete-champ-${c.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ChampionshipForm
          champ={editing}
          createKind={createKind}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ChampionshipForm({ champ, createKind = "community", onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyChamp, ...champ, points: champ.points || DEFAULT_POINTS });
  const [qualiEnabled, setQualiEnabled] = useState(!!(champ.quali_points && Object.keys(champ.quali_points).length));
  const [qualiPoints, setQualiPoints] = useState(
    champ.quali_points && Object.keys(champ.quali_points).length ? champ.quali_points : { ...DEFAULT_POINTS }
  );
  const [saving, setSaving] = useState(false);
  const isEdit = !!champ.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPoint = (pos, val) => setForm({ ...form, points: { ...form.points, [pos]: parseInt(val || 0, 10) } });
  const setQualiPoint = (pos, val) => setQualiPoints({ ...qualiPoints, [pos]: parseInt(val || 0, 10) });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    const payload = {
      name: form.name, category: form.category,
      platform: form.category === "sim" ? form.platform : null,
      description: form.description, image_url: form.image_url,
      regulation: form.regulation, points: form.points, kind: createKind,
      cost: parseInt(form.cost || 0, 10),
      access: form.access || "open",
      quali_points: qualiEnabled ? qualiPoints : null,
    };
    try {
      if (isEdit) await api.put(`/championships/${champ.id}`, payload);
      else await api.post("/championships", payload);
      toast.success(isEdit ? "Campionato aggiornato" : "Campionato creato");
      onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Modifica campionato" : "Nuovo campionato"} onClose={onClose} testid="championship-form">
      <div className="space-y-4">
        <div>
          <Label>Nome</Label>
          <Input data-testid="champ-name" value={form.name} onChange={set("name")} className="mt-2" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Categoria</Label>
            <Select data-testid="champ-category" value={form.category} onChange={set("category")} className="mt-2">
              <option value="rc">Corse RC fisiche</option>
              <option value="sim">Simulatore</option>
            </Select>
          </div>
          {form.category === "sim" && (
            <div>
              <Label>Piattaforma</Label>
              <Select data-testid="champ-platform" value={form.platform || ""} onChange={set("platform")} className="mt-2">
                <option value="">Seleziona</option>
                <option value="PlayStation">PlayStation</option>
                <option value="Xbox">Xbox</option>
                <option value="PC">PC</option>
              </Select>
            </div>
          )}
        </div>
        <div>
          <Label>Descrizione</Label>
          <Textarea data-testid="champ-description" rows={2} value={form.description || ""} onChange={set("description")} className="mt-2" />
        </div>
        <div>
          <Label>Costo iscrizione (€) — 0 = non mostrato</Label>
          <Input type="number" min="0" data-testid="champ-cost" value={form.cost ?? 0} onChange={set("cost")} className="mt-2" placeholder="0" />
        </div>
        <div>
          <Label>Accesso al campionato</Label>
          <div className="mt-2 flex flex-col gap-2" data-testid="champ-access">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
              <input type="radio" name="access" checked={(form.access || "open") === "open"} onChange={() => setForm({ ...form, access: "open" })} className="accent-yellow-400" data-testid="access-open" />
              Aperto — chiunque può iscriversi liberamente
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
              <input type="radio" name="access" checked={form.access === "restricted"} onChange={() => setForm({ ...form, access: "restricted" })} className="accent-yellow-400" data-testid="access-restricted" />
              Ristretto — ogni iscrizione va approvata dall'organizzatore
            </label>
          </div>
        </div>
        <div>
          <Label>Immagine copertina</Label>
          <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} testid="champ-image" />
        </div>
        <div>
          <Label>Regolamento</Label>
          <Textarea data-testid="champ-regulation" rows={4} value={form.regulation || ""} onChange={set("regulation")} className="mt-2" placeholder="Regole del campionato…" />
        </div>
        <div>
          <Label>Punteggio per posizione</Label>
          <div className="mt-2 grid grid-cols-5 gap-2">
            {Object.keys(DEFAULT_POINTS).map((pos) => (
              <div key={pos} className="text-center">
                <div className="text-[10px] text-zinc-500 mb-1">P{pos}</div>
                <Input type="number" data-testid={`points-${pos}`} value={form.points[pos] ?? 0}
                  onChange={(e) => setPoint(pos, e.target.value)} className="text-center px-1" />
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="quali-points-toggle" checked={qualiEnabled}
              onChange={(e) => setQualiEnabled(e.target.checked)}
              className="h-4 w-4 accent-yellow-400" />
            <Label>Assegna punti anche alla Qualifica (punteggio dedicato)</Label>
          </label>
          {qualiEnabled && (
            <div className="mt-3 grid grid-cols-5 gap-2" data-testid="quali-points-grid">
              {Object.keys(DEFAULT_POINTS).map((pos) => (
                <div key={pos} className="text-center">
                  <div className="text-[10px] text-cyan-400 mb-1">Q{pos}</div>
                  <Input type="number" data-testid={`quali-points-${pos}`} value={qualiPoints[pos] ?? 0}
                    onChange={(e) => setQualiPoint(pos, e.target.value)} className="text-center px-1" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <GhostBtn onClick={onClose}>Annulla</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="save-champ-btn">
            {saving ? "Salvataggio…" : "Salva"}
          </PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function EnrollmentRequests({ champId }) {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get(`/championships/${champId}/enrollment-requests`).then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [champId]); // eslint-disable-line
  const act = async (id, action) => {
    try { await api.post(`/enrollments/${id}/${action}`); toast.success(action === "approve" ? "Iscrizione approvata" : "Richiesta rifiutata"); load(); }
    catch { toast.error("Errore"); }
  };
  if (reqs.length === 0) return null;
  return (
    <div className="mb-6 border border-amber-500/40 bg-amber-500/5 p-5" data-testid="enrollment-requests">
      <h3 className="flex items-center gap-2 font-heading text-sm font-bold uppercase text-amber-300 mb-3">
        <Clock className="h-4 w-4" /> Approvazioni ({reqs.length})
      </h3>
      <div className="space-y-2">
        {reqs.map((r) => (
          <div key={r.id} data-testid={`req-${r.id}`} className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-900 px-4 py-3">
            <div className="min-w-0">
              <span className="font-heading font-bold text-zinc-50">{r.name}</span>
              <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{r.type === "event" ? "GP" : "Campionato"} · {r.target_name}</span>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 border border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs font-bold uppercase px-3 py-2 transition-colors duration-200"><CheckCircle2 className="h-4 w-4" /> Approva</button>
              <button onClick={() => act(r.id, "reject")} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-bold uppercase px-3 py-2 transition-colors duration-200"><XCircle className="h-4 w-4" /> Rifiuta</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsManager({ champ, onBack }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);
  const [chooseFor, setChooseFor] = useState(null);

  const openResults = (e) => {
    const s = e.session_types && e.session_types.length ? e.session_types : [e.session_type || "gara"];
    if (s.length > 1) setChooseFor(e);
    else setResultsFor({ event: e, session: s[0] });
  };

  const emptyEvent = { championship_id: champ.id, name: "", session_types: ["gara"], date: "", time: "", track: "", location: "", description: "" };
  const load = () => api.get(`/championships/${champ.id}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Eliminare il Gran Premio e i suoi risultati?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Gran Premio eliminato");
    load();
  };

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-champs"
        className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
        <ChevronLeft className="h-4 w-4" /> Campionati
      </button>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold uppercase text-zinc-100">{champ.name}</h2>
          <p className="text-xs text-zinc-500">Gran Premi e risultati</p>
        </div>
        <PrimaryBtn onClick={() => setEditing({ ...emptyEvent })} data-testid="new-event-btn">
          <Plus className="h-4 w-4" /> Nuovo Gran Premio
        </PrimaryBtn>
      </div>

      <EnrollmentRequests champId={champ.id} />

      {events.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Nessun Gran Premio.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className="border border-zinc-800 bg-zinc-900 p-5">
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-cyan-400">
                {(e.session_types || [e.session_type]).map((v) => SESSION_OPTS.find((s) => s.v === v)?.l).filter(Boolean).join(" · ") || "Gara"}
              </span>
              <h3 className="font-heading text-lg font-bold text-zinc-50">{e.name}</h3>
              <p className="text-xs text-zinc-500">{[e.date, e.time, e.track || e.location].filter(Boolean).join(" · ") || "—"}</p>
              <div className="mt-4 flex gap-2">
                <PrimaryBtn onClick={() => openResults(e)} data-testid={`results-event-${e.id}`} className="flex-1">
                  Risultati
                </PrimaryBtn>
                <button onClick={() => setEditing(e)} data-testid={`edit-event-${e.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-yellow-400 hover:border-yellow-400 transition-colors duration-200">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(e.id)} data-testid={`delete-event-${e.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventForm event={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {chooseFor && (
        <Modal title={`Risultati · ${chooseFor.name}`} onClose={() => setChooseFor(null)} testid="session-chooser">
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Scegli il tipo di sessione da modificare:</p>
            <div className="flex flex-wrap gap-3">
              {(chooseFor.session_types && chooseFor.session_types.length ? chooseFor.session_types : [chooseFor.session_type || "gara"]).map((s) => (
                <PrimaryBtn key={s} data-testid={`choose-session-${s}`}
                  onClick={() => { setResultsFor({ event: chooseFor, session: s }); setChooseFor(null); }}>
                  {SESSION_OPTS.find((o) => o.v === s)?.l || s}
                </PrimaryBtn>
              ))}
            </div>
          </div>
        </Modal>
      )}
      {resultsFor && (
        <ResultsEditor event={resultsFor.event} initialSession={resultsFor.session}
          onBack={(resultsFor.event.session_types && resultsFor.event.session_types.length > 1)
            ? () => { setChooseFor(resultsFor.event); setResultsFor(null); } : undefined}
          onClose={() => setResultsFor(null)} />
      )}
    </div>
  );
}

function EventForm({ event, onClose, onSaved }) {
  const [form, setForm] = useState({ ...event, session_types: event.session_types || (event.session_type ? [event.session_type] : ["gara"]) });
  const [saving, setSaving] = useState(false);
  const isEdit = !!event.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleSession = (v) => {
    const cur = form.session_types || [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    setForm({ ...form, session_types: next });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome"); return; }
    const stypes = form.session_types && form.session_types.length ? form.session_types : ["gara"];
    setSaving(true);
    const payload = {
      championship_id: form.championship_id, name: form.name,
      session_types: stypes, session_type: stypes[0],
      date: form.date, time: form.time || "", track: form.track || "",
      location: form.location, car_model: form.car_model || "", cost: parseInt(form.cost || 0, 10),
      description: form.description || "",
      duration_type: form.duration_type || null,
      duration_value: form.duration_value ? parseInt(form.duration_value, 10) : null,
      stream_url: form.stream_url || "", server_name: form.server_name || "", server_password: form.server_password || "", game_name: form.game_name || "",
    };
    try {
      if (isEdit) await api.put(`/events/${event.id}`, payload);
      else await api.post("/events", payload);
      toast.success(isEdit ? "Gran Premio aggiornato" : "Gran Premio creato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Modifica Gran Premio" : "Nuovo Gran Premio"} onClose={onClose} testid="event-form">
      <div className="space-y-4">
        <div>
          <Label>Nome Gran Premio</Label>
          <Input data-testid="event-name" value={form.name} onChange={set("name")} className="mt-2" />
        </div>
        <div>
          <Label>Tipo sessione (scelta multipla)</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {SESSION_OPTS.map((s) => {
              const active = (form.session_types || []).includes(s.v);
              return (
                <button type="button" key={s.v} data-testid={`event-session-${s.v}`} onClick={() => toggleSession(s.v)}
                  className={`px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase border transition-colors duration-200 ${
                    active ? "border-yellow-400 bg-yellow-400/10 text-yellow-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}>
                  {s.l}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Data</Label>
            <Input data-testid="event-date" value={form.date || ""} onChange={set("date")} className="mt-2" placeholder="es. 12/07/2026" />
          </div>
          <div>
            <Label>Ora</Label>
            <Input data-testid="event-time" value={form.time || ""} onChange={set("time")} className="mt-2" placeholder="es. 15:30" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Durata gara</Label>
            <select data-testid="event-duration-type" value={form.duration_type || ""} onChange={set("duration_type")}
              className="mt-2 w-full bg-zinc-950 border border-zinc-700 text-zinc-100 px-3 py-2.5 text-sm outline-none focus:border-yellow-400">
              <option value="">— Nessuna</option>
              <option value="tempo">A tempo (minuti)</option>
              <option value="giri">A giri</option>
            </select>
          </div>
          <div>
            <Label>{form.duration_type === "giri" ? "Numero giri" : "Minuti"}</Label>
            <Input type="number" data-testid="event-duration-value" value={form.duration_value || ""} onChange={set("duration_value")} className="mt-2"
              placeholder={form.duration_type === "giri" ? "es. 20" : "es. 15"} disabled={!form.duration_type} />
          </div>
        </div>
        <div>
          <Label>Link diretta (dove viene trasmessa)</Label>
          <Input data-testid="event-stream-url" value={form.stream_url || ""} onChange={set("stream_url")} className="mt-2" placeholder="https://twitch.tv/…  o  youtube.com/…" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Nome server (dove unirsi)</Label>
            <Input data-testid="event-server-name" value={form.server_name || ""} onChange={set("server_name")} className="mt-2" placeholder="es. TUONO-01" />
          </div>
          <div>
            <Label>Password server</Label>
            <Input data-testid="event-server-password" value={form.server_password || ""} onChange={set("server_password")} className="mt-2" placeholder="es. tuono2026" />
          </div>
        </div>
        <div>
          <Label>Nome gioco (simulatore)</Label>
          <Input data-testid="event-game-name" value={form.game_name || ""} onChange={set("game_name")} className="mt-2" placeholder="es. Assetto Corsa" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Pista</Label>
            <Input data-testid="event-track" value={form.track || ""} onChange={set("track")} className="mt-2" placeholder="es. Autodromo di Monza" />
          </div>
          <div>
            <Label>Luogo</Label>
            <Input data-testid="event-location" value={form.location || ""} onChange={set("location")} className="mt-2" placeholder="es. Monza (MB)" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Modello di macchina</Label>
            <Input data-testid="event-car-model" value={form.car_model || ""} onChange={set("car_model")} className="mt-2" placeholder="es. Team Associated RC10B7" />
          </div>
          <div>
            <Label>Costo (€) — 0 = non mostrato</Label>
            <Input type="number" min="0" data-testid="event-cost" value={form.cost ?? 0} onChange={set("cost")} className="mt-2" placeholder="0" />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <GhostBtn onClick={onClose}>Annulla</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="save-event-btn">
            {saving ? "Salvataggio…" : "Salva"}
          </PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}
